// 개선 9건 차수1(26-08-04) §4 (결정 ③A + '완료본도' 오너 결정) — 페이지 골라 받기.
//
// 재고팀이 인수증의 **액세서리 페이지만** 받아야 해서 만들었다. 페이지 번호를
// 어디에도 박지 않는다: 다운로드가 뽑을 그 문서를 그대로 받아 썸네일을 그리고,
// 사람이 눈으로 고른 번호를 서버에 보낸다(액세서리는 양식에 따라 3페이지이기도
// 4페이지이기도 하다 — 실례 확인).
//
// ⚠ 미리보기와 다운로드가 **같은 경로**(fetchDocumentPdfBytes ↔ downloadDocumentPages)
// 를 쓰는 것이 이 화면의 핵심이다. 상세 DTO 의 pdfDownloadUrl(진행 중이면 베이스
// 양식)로 썸네일을 그리면 첨부가 붙은 문서에서 번호가 한 칸씩 밀려 엉뚱한 페이지가
// 나간다.
//
// UI 상태 3종(계획 §4 A-4 명시):
//   ① 썸네일 로딩 중 = 번호 + 회색 자리표시, **체크 가능**(그림을 기다릴 필요 없다)
//   ② 렌더 실패     = 번호 + '미리보기 불가', **선택 유지**(그림만 못 그린 것)
//   ③ 선택 0페이지  = 다운로드 버튼 비활성 + "페이지를 골라 주세요"

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { withPageSuffix } from '@bhops/shared';
import { signatureDocumentsApi, type SignPdfVariant } from '@/api/signatureDocuments';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';

// PdfCanvasViewer 와 같은 관례 — 한 번만, HMR 에도 멱등.
if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
}

// 썸네일 크기(px). A4 비율(794:1122)을 유지해 자리표시와 실제 그림이 같은 칸을 쓴다.
const THUMB_W = 132;
const THUMB_H = Math.round((THUMB_W * 1122) / 794);

type ThumbState = 'loading' | 'ready' | 'failed';

interface Props {
  documentId: string;
  /** 'prefill' = 진행 중 합성본 · 'final' = 완료 서명본. */
  variant: SignPdfVariant;
  /**
   * 저장 파일명(확장자 포함). 서버 헤더가 아니라 이 값이 실제 저장명이 된다 —
   * api/signatureDocuments.ts 의 fallbackName 주석 참고.
   */
  fallbackName: string;
}

export function SignDocumentPagePicker({ documentId, variant, fallbackName }: Props) {
  const toast = useToast();
  const [pageCount, setPageCount] = useState(0);
  const [thumbs, setThumbs] = useState<ThumbState[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set<number>());
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);

  // 다운로드가 뽑을 그 문서를 그대로 받는다(번호 정합의 유일한 방법).
  const srcQ = useQuery({
    queryKey: ['sign-doc-page-src', documentId, variant],
    queryFn: () => signatureDocumentsApi.fetchDocumentPdfBytes(documentId, variant),
    // 합성 비용이 있는 경로라 창을 여는 동안은 재요청하지 않는다.
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const bytes = srcQ.data;

  useEffect(() => {
    if (!bytes) return;
    let cancelled = false;
    let task: { cancel: () => void } | null = null;

    (async () => {
      let pdf: Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>;
      try {
        // pdfjs 가 버퍼를 detach 할 수 있어 사본을 넘긴다(재조회 없이 재시도 가능).
        pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes.slice(0)) }).promise;
      } catch {
        // 문서 자체를 열지 못하면 페이지 수를 모른다 — 아래 '문서를 열 수 없습니다'.
        if (!cancelled) {
          setPageCount(0);
          setThumbs([]);
        }
        return;
      }
      if (cancelled) return;
      // 페이지 수를 먼저 확정한다 → 체크박스가 그림보다 먼저 나온다(상태 ①).
      setPageCount(pdf.numPages);
      setThumbs(Array.from({ length: pdf.numPages }, () => 'loading' as ThumbState));

      for (let n = 1; n <= pdf.numPages; n += 1) {
        if (cancelled) return;
        try {
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: THUMB_W / base.width });
          // canvas 가 붙을 프레임을 한 번 양보(PdfCanvasViewer 와 같은 관례).
          await new Promise<void>((r) => requestAnimationFrame(() => r()));
          if (cancelled) return;
          const canvas = canvasRefs.current[n - 1];
          const ctx = canvas?.getContext('2d');
          if (!canvas || !ctx) throw new Error('canvas 2d context 없음');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          const t = page.render({ canvasContext: ctx, viewport });
          task = t;
          await t.promise;
          if (cancelled) return;
          setThumbs((prev) => withState(prev, n, 'ready'));
        } catch {
          // 상태 ② — 그림만 실패한 것이다. 선택은 건드리지 않는다.
          if (!cancelled) setThumbs((prev) => withState(prev, n, 'failed'));
        }
      }
    })();

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [bytes]);

  const pickedPages = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);

  const downloadMutation = useMutation({
    mutationFn: () =>
      signatureDocumentsApi.downloadDocumentPages(
        documentId,
        variant,
        pickedPages,
        pageFileName(fallbackName, pickedPages),
      ),
    onSuccess: () => toast.success(`${pickedPages.join(', ')}페이지를 저장했습니다.`),
    onError: (err) => toast.error(extractApiError(err), '페이지 다운로드 실패'),
  });

  function toggle(page: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page);
      else next.add(page);
      return next;
    });
  }

  if (srcQ.isLoading) {
    return (
      <div
        style={{ fontSize: 13, color: 'var(--text-secondary)' }}
        data-testid="sign-doc-page-src-loading"
      >
        문서를 불러오는 중…
      </div>
    );
  }

  if (srcQ.isError) {
    return (
      <div
        style={{ fontSize: 13, color: 'var(--color-error)' }}
        data-testid="sign-doc-page-src-error"
      >
        {extractApiError(srcQ.error)}
      </div>
    );
  }

  if (pageCount === 0) {
    return (
      <div
        style={{ fontSize: 13, color: 'var(--color-error)' }}
        data-testid="sign-doc-page-src-broken"
      >
        문서를 열 수 없어 페이지를 고를 수 없습니다.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        받을 페이지를 골라 주세요. 전체 {pageCount}페이지 중 고른 페이지만 PDF 한 개로 저장됩니다.
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => {
          const state = thumbs[n - 1] ?? 'loading';
          const isPicked = selected.has(n);
          return (
            <label
              key={n}
              style={{
                ...pageCard,
                borderColor: isPicked ? 'var(--fg-brand)' : 'var(--border-default)',
                background: isPicked ? 'var(--bg-info-subtle)' : 'var(--bg-surface)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={isPicked}
                  onChange={() => toggle(n)}
                  aria-label={`${n}페이지 선택`}
                  data-testid={`sign-doc-page-check-${n}`}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: 12, fontWeight: 600 }}>{n}페이지</span>
              </div>
              <div style={{ ...thumbBox, width: THUMB_W, height: THUMB_H }}>
                {/* 자리표시(상태 ①)와 실패 문구(상태 ②)는 canvas 위에 겹치지 않고
                    같은 칸을 대신 차지한다 — 체크는 어느 상태에서도 눌린다. */}
                {state === 'failed' ? (
                  <span
                    style={{ fontSize: 11, color: 'var(--text-secondary)' }}
                    data-testid={`sign-doc-page-thumb-failed-${n}`}
                  >
                    미리보기 없음
                  </span>
                ) : (
                  <>
                    {state === 'loading' && (
                      <span
                        style={placeholder}
                        aria-hidden
                        data-testid={`sign-doc-page-thumb-loading-${n}`}
                      />
                    )}
                    <canvas
                      ref={(el) => {
                        canvasRefs.current[n - 1] = el;
                      }}
                      style={{
                        display: state === 'ready' ? 'block' : 'none',
                        width: THUMB_W,
                        height: THUMB_H,
                      }}
                    />
                  </>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => downloadMutation.mutate()}
          disabled={pickedPages.length === 0 || downloadMutation.isPending}
          data-testid="sign-doc-page-download"
          style={{
            fontSize: 12,
            padding: '6px 12px',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--r-btn)',
            cursor: pickedPages.length === 0 ? 'not-allowed' : 'pointer',
            opacity: pickedPages.length === 0 ? 0.5 : 1,
          }}
        >
          {downloadMutation.isPending
            ? '만드는 중…'
            : pickedPages.length > 0
              ? `${pickedPages.join(', ')}페이지 다운로드`
              : '고른 페이지 다운로드'}
        </button>
        {/* 상태 ③ — 왜 눌리지 않는지 그 자리에서 말한다. */}
        {pickedPages.length === 0 && (
          <span
            style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            data-testid="sign-doc-page-none-hint"
          >
            페이지를 골라 주세요.
          </span>
        )}
      </div>
    </div>
  );
}

function withState(prev: ThumbState[], page: number, state: ThumbState): ThumbState[] {
  const next = [...prev];
  next[page - 1] = state;
  return next;
}

/**
 * 저장 파일명에 고른 페이지를 적는다.
 *
 * 개선 9건 차수2b(26-08-04) 결함④ — 종전엔 이 함수가 서버의 표기 알고리즘("연속
 * 3장 이상은 범위로 접는다")을 **한 글자씩 복제**해 갖고 있었다. 그런데 서버가
 * `Content-Disposition` 을 보내도 downloadBlob 은 `filename="…"` 만 파싱하므로 이
 * 사본이 실제 저장 이름을 결정한다 — 어긋나면 서버 로그·감사와 재고팀 폴더의 파일이
 * 다른 이름을 갖는다. 이제 **서버와 같은 함수**(shared 정본)를 부른다.
 *
 * 얇은 래퍼를 남기는 이유는 이름뿐이다 — 이 화면의 호출부·시험이 부르는 이름을
 * 유지하고, "고른 게 없으면 원래 이름"이라는 이 화면의 의미를 그대로 읽히게 한다.
 */
export function pageFileName(fallbackName: string, pages: ReadonlyArray<number>): string {
  return withPageSuffix(fallbackName, pages);
}

const pageCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 8,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  cursor: 'pointer',
};

const thumbBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-sunken)',
  borderRadius: 4,
  overflow: 'hidden',
};

const placeholder: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  background: 'var(--bg-sunken)',
};
